import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChartLine, ClipboardText, Lightbulb, TaskDaily, TrendUp } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import Briefing from '../components/marketIntelligence/Briefing';
import CampaignCalendar from '../components/marketIntelligence/CampaignCalendar';
import DemandForecast from '../components/marketIntelligence/DemandForecast';
import IntelligenceReports from '../components/marketIntelligence/IntelligenceReports';
import Opportunities from '../components/marketIntelligence/Opportunities';
import PriceElasticity from '../components/marketIntelligence/PriceElasticity';

// Página própria da Inteligência de Mercado (Fase 4): promovida do grupo
// "Inteligência" da página de campanhas para rota dedicada. Aqui a navegação
// é de UM nível só — os 6 painéis são irmãos, sem grupos — mas o visual das
// abas é o MESMO do nível 1 de Campaigns.jsx (border-b + ícone), para o
// usuário não sentir troca de linguagem entre as duas páginas.
const TABS = [
    { id: 'briefing', label: 'Briefing', icon: TaskDaily },
    { id: 'reports', label: 'Relatórios', icon: ClipboardText },
    { id: 'opportunities', label: 'Oportunidades', icon: Lightbulb },
    { id: 'calendar', label: 'Calendário', icon: Calendar },
    { id: 'elasticity', label: 'Elasticidade', icon: ChartLine },
    { id: 'demand', label: 'Demanda', icon: TrendUp },
];

const MarketIntelligence = () => {
    const navigate = useNavigate();
    // Briefing primeiro = a leitura do dia é a porta de entrada da página.
    const [activeTab, setActiveTab] = useState('briefing');
    // Relatório ativo da aba Relatórios. Vive na PÁGINA porque os cards do
    // Briefing navegam direto para um relatório específico ("Perdendo
    // catálogo" abre o relatório de buybox) — o filho não pode ser dono da
    // escolha que outro filho precisa mudar.
    const [intelReport, setIntelReport] = useState('summary');

    // Navegação vinda dos cards do Briefing. Ele fala os ids históricos da
    // época em que morava dentro de Campaigns ('intel-reports', 'attention');
    // traduzir AQUI evita reescrever os componentes movidos: 'attention'
    // continua vivendo na página Análise Meli (deep-link via query param) e o
    // prefixo 'intel-' cai porque as abas daqui não têm grupo.
    const goToTab = useCallback(
        ({ tab, report = null }) => {
            if (tab === 'attention') {
                navigate('/campaigns?tab=attention');
                return;
            }
            if (report) {
                setIntelReport(report);
            }
            setActiveTab(tab.replace(/^intel-/, ''));
        },
        [navigate],
    );

    return (
        <div className="space-y-6">
            <PageHeader
                title="Inteligência de Mercado"
                subtitle="Briefing diário, relatórios e análises de campanhas e concorrência"
            />

            {/* Abas de nível único — mesmo padrão visual do nível 1 de Campaigns.jsx */}
            <div className="border-b border-mono-200 dark:border-mono-800">
                <nav className="flex space-x-8 overflow-x-auto">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = tab.id === activeTab;
                        return (
                            <button
                                type="button"
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                aria-current={isActive ? 'true' : undefined}
                                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center gap-2 ${
                                    isActive
                                        ? 'border-black dark:border-white text-black dark:text-white'
                                        : 'border-transparent text-mono-500 hover:text-mono-700 dark:hover:text-mono-300 hover:border-mono-300'
                                }`}
                            >
                                <Icon size={18} />
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Render condicional de propósito: montar ao entrar na aba é o que
                garante o refetch a cada visita (o fetch vive no mount de cada
                painel). */}
            {activeTab === 'briefing' && <Briefing onNavigate={goToTab} />}
            {activeTab === 'reports' && <IntelligenceReports report={intelReport} onReportChange={setIntelReport} />}
            {activeTab === 'opportunities' && <Opportunities />}
            {activeTab === 'calendar' && <CampaignCalendar />}
            {activeTab === 'elasticity' && <PriceElasticity />}
            {activeTab === 'demand' && <DemandForecast />}
        </div>
    );
};

export default MarketIntelligence;
